import {ReactElement} from 'react'
import {Trans} from 'react-i18next'
import {WithdrawalVendorEnumIndices, withdrawalPaymentVendors} from '@bdswiss/common-enums'

import messages from '../../../assets/messages'
import {Account} from '../../../graphql/types'
import {
  getTauroFactorisationFromAccount,
  TauroAccountFactorisation,
  TauroRefundMethod,
} from '../../../common/utils/accounts'
import {WithdrawalMethodValues} from './WithdrawalSummary/constants'
import {RefundWithdrawSchemaItem} from './WithdrawalSummary/types'

/** The payout leg the server expects on every item of a factorised withdrawal (PT-6618). */
export enum TauroPayoutLeg {
  refund = 'REFUND',
  profit = 'PROFIT',
}

/**
 * The profit leg goes to Crypto, Bank Transfer or Pix payout, the same rule the server applies.
 * The withdrawal options of the account list the Pix payout for a client from Brazil only.
 */
export const TAURO_PROFIT_METHODS: string[] = [
  WithdrawalMethodValues.bankTransfer,
  WithdrawalMethodValues.cryptocurrency,
  WithdrawalMethodValues.enovipayCryptoPayout,
  WithdrawalMethodValues.dfnsCryptoPayout,
  WithdrawalMethodValues.enovipayPixPayout,
]

/** Refund vendors that the withdrawal form can send without extra client input. */
const TAURO_REFUND_VENDORS: string[] = [
  WithdrawalVendorEnumIndices.enovipayRefund,
  WithdrawalVendorEnumIndices.enovipayMobileWalletRefund,
  WithdrawalVendorEnumIndices.enovipayKnetRefund,
]

/**
 * The factorisation of a funded Tauro account, or null for a regular or unfunded account.
 * A funded account withdraws only through the one-time payout flow (PT-6615).
 */
export const getTauroWithdrawal = (
  account: Account | null | undefined,
): TauroAccountFactorisation | null => {
  if (!account) return null
  const factorisation = getTauroFactorisationFromAccount(account)
  return factorisation?.clientPayout != null ? factorisation : null
}

/**
 * The refund leg as a refund schema item, or null when the account has no refund leg: the server
 * gives no refund method and a zero refund payout when this form cannot send the refund of the
 * first deposit, and the whole payout then goes to a payout method. The card refund needs the
 * last card digits in its payment fields.
 */
export const buildTauroRefundItem = (
  factorisation: TauroAccountFactorisation,
): RefundWithdrawSchemaItem | null => {
  const {refundMethod, refundPayout} = factorisation
  const vendor = refundMethod?.paymentVendor
  if (!vendor || !TAURO_REFUND_VENDORS.includes(vendor) || !refundPayout) return null
  if (vendor === WithdrawalVendorEnumIndices.enovipayRefund && !refundMethod?.cardNumber) return null
  return {
    id: 'tauro-refund',
    amount: refundPayout,
    details: refundMethod?.cardNumber || '',
    vendor,
  }
}

/**
 * True when the server sized a refund leg that this form cannot send: a refund payout with no
 * refund item. The request would fail the server payout legs check, so the form blocks it.
 */
export const isTauroRefundUnavailable = (factorisation: TauroAccountFactorisation): boolean =>
  Boolean(factorisation.refundPayout) && !buildTauroRefundItem(factorisation)

/** Masked name of the original payment method, e.g. "Card •••• 4821". */
export const TauroRefundMethodName = ({
  refundMethod,
}: {
  refundMethod: TauroRefundMethod | null | undefined
}): ReactElement | null => {
  if (!refundMethod?.paymentVendor) return null
  if (refundMethod.cardNumber) {
    return (
      <Trans {...messages.tauroRefundMethodCard} values={{cardNumber: refundMethod.cardNumber}} />
    )
  }
  return <>{withdrawalPaymentVendors[refundMethod.paymentVendor]?.label ?? refundMethod.paymentVendor}</>
}
